import React from "react";
import { PaperClipOutlined } from "@ant-design/icons";
import { Typography } from "antd";

import { type SupportRequestAttachment } from "@fitness-app/data-models";

import { HeicImage } from "~/components/HeicImage/HeicImage";

// Trello imports may miss mimeType — fall back to the file name / signed URL path.
const IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|gif|webp|heic|heif|avif|bmp)($|\?)/i;

export const isImageAttachment = (attachment: SupportRequestAttachment) =>
  attachment.mimeType
    ? attachment.mimeType.startsWith("image/")
    : IMAGE_EXTENSION_PATTERN.test(attachment.name) || IMAGE_EXTENSION_PATTERN.test(attachment.url);

interface SupportAttachmentListProps {
  attachments: SupportRequestAttachment[];
  // Thumbnail edge in px — the drawer section uses the default, comments go smaller.
  thumbnailSize?: number;
}

// Shared renderer for request and comment attachments: images become clickable
// thumbnails with a full-screen preview, other files stay plain download links.
export const SupportAttachmentList: React.FC<SupportAttachmentListProps> = ({ attachments, thumbnailSize = 88 }) => {
  if (!attachments.length) {
    return null;
  }

  const images = attachments.filter(isImageAttachment);
  const files = attachments.filter((attachment) => !isImageAttachment(attachment));

  return (
    <div className="space-y-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {images.map((attachment, index) => (
            <HeicImage
              key={`${attachment.url}-${index}`}
              src={attachment.url}
              alt={attachment.name}
              title={attachment.name}
              width={thumbnailSize}
              height={thumbnailSize}
              className="rounded-md border border-gray-200 object-cover"
            />
          ))}
        </div>
      )}
      {files.map((attachment, index) => (
        <Typography.Link
          key={`${attachment.url}-${index}`}
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1"
        >
          <PaperClipOutlined /> {attachment.name || `Załącznik ${index + 1}`}
        </Typography.Link>
      ))}
    </div>
  );
};
